const root = document.documentElement;
const header = document.querySelector("[data-header]");
const progress = document.querySelector("[data-progress]");
const hero = document.querySelector("[data-hero]");
const deviceStage = document.querySelector("[data-device]");
const device = deviceStage?.querySelector(".device");
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const mobileHero = window.matchMedia("(max-width: 640px)");
let heroProgress = mobileHero.matches ? .62 : .28;
let heroInteracted = false;
let heroIntroFrame = 0;
let touchState = null;
let pendingHeroProgress = null;
let heroProgressFrame = 0;
let heroRect = null;
let heroVisible = true;
let heroResetTimer = 0;

function clamp(value, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value));
}

function applyHeroProgress(value) {
  if (!hero) return;
  heroProgress = clamp(value);
  const size = window.innerHeight * (.16 + heroProgress * .6);
  root.style.setProperty("--open-progress", heroProgress.toFixed(4));
  root.style.setProperty("--aperture", `${size}px`);
  root.style.setProperty("--aperture-half", `${size / 2}px`);
}

function scheduleHeroProgress(value) {
  pendingHeroProgress = value;
  if (heroProgressFrame) return;
  heroProgressFrame = requestAnimationFrame(() => {
    applyHeroProgress(pendingHeroProgress);
    heroProgressFrame = 0;
  });
}

function cacheHeroRect() {
  heroRect = hero?.getBoundingClientRect() || null;
}

function cancelHeroReset() {
  if (!heroResetTimer) return;
  clearTimeout(heroResetTimer);
  heroResetTimer = 0;
}

function scheduleHeroReset() {
  cancelHeroReset();
  heroResetTimer = window.setTimeout(() => {
    hero?.classList.remove("is-interacting");
    scheduleHeroProgress(.4);
    heroResetTimer = 0;
  }, 300);
}

function updateScroll() {
  const y = window.scrollY;
  const available = document.documentElement.scrollHeight - window.innerHeight;
  heroRect = null;
  header?.classList.toggle("is-scrolled", y > 32);
  if (progress) progress.style.transform = `scaleX(${available > 0 ? y / available : 0})`;
}

function setHeroAperture(event) {
  if (!hero || !heroVisible || mobileHero.matches || reducedMotion.matches || event.pointerType === "touch") return;
  const rect = heroRect || hero.getBoundingClientRect();
  const top = Math.max(0, rect.top - 96);
  const bottom = Math.min(window.innerHeight, rect.bottom + 64);
  if (event.clientY < top || event.clientY > bottom) return;
  cancelHeroReset();
  heroInteracted = true;
  cancelAnimationFrame(heroIntroFrame);
  hero.classList.add("is-interacting");
  scheduleHeroProgress((event.clientX - rect.left) / rect.width);
}

function beginHeroTouch(event) {
  if (!hero || !heroVisible || mobileHero.matches || reducedMotion.matches || event.pointerType !== "touch") return;
  heroInteracted = true;
  cancelAnimationFrame(heroIntroFrame);
  touchState = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    startProgress: heroProgress,
    direction: null
  };
}

function moveHeroTouch(event) {
  if (!hero || !touchState || event.pointerId !== touchState.pointerId) return;
  const dx = event.clientX - touchState.startX;
  const dy = event.clientY - touchState.startY;

  if (!touchState.direction && Math.hypot(dx, dy) > 8) {
    touchState.direction = Math.abs(dx) > Math.abs(dy) * 1.15 ? "horizontal" : "vertical";
  }
  if (touchState.direction !== "horizontal") return;

  event.preventDefault();
  hero.classList.add("is-interacting");
  const rect = heroRect || hero.getBoundingClientRect();
  scheduleHeroProgress(touchState.startProgress + dx / (rect.width * .72));
}

function endHeroTouch(event) {
  if (!touchState || event.pointerId !== touchState.pointerId) return;
  touchState = null;
  hero?.classList.remove("is-interacting");
}

function runHeroIntro(time) {
  if (!hero || !heroVisible || mobileHero.matches || heroInteracted || reducedMotion.matches) return;
  const start = Number(hero.dataset.introStart || time + 320);
  hero.dataset.introStart = String(start);
  const elapsed = time - start;

  if (elapsed < 0) {
    heroIntroFrame = requestAnimationFrame(runHeroIntro);
    return;
  }

  if (elapsed <= 1050) {
    const t = 1 - Math.pow(1 - clamp(elapsed / 1050), 3);
    scheduleHeroProgress(.24 + (.62 - .24) * t);
    heroIntroFrame = requestAnimationFrame(runHeroIntro);
    return;
  }

  if (elapsed <= 1600) {
    const t = 1 - Math.pow(1 - clamp((elapsed - 1050) / 550), 3);
    scheduleHeroProgress(.62 + (.4 - .62) * t);
    heroIntroFrame = requestAnimationFrame(runHeroIntro);
    return;
  }

  applyHeroProgress(.4);
}

function tiltDevice(event) {
  if (!deviceStage || !device || reducedMotion.matches || event.pointerType === "touch") return;
  const rect = deviceStage.getBoundingClientRect();
  const rx = ((event.clientY - rect.top) / rect.height - .5) * -6;
  const ry = ((event.clientX - rect.left) / rect.width - .5) * 10;
  device.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg)`;
}

hero?.addEventListener("pointerenter", cacheHeroRect);
hero?.addEventListener("pointerdown", beginHeroTouch);
hero?.addEventListener("pointermove", moveHeroTouch, { passive: false });
hero?.addEventListener("pointerup", endHeroTouch);
hero?.addEventListener("pointercancel", endHeroTouch);
window.addEventListener("pointermove", setHeroAperture, { passive: true });
window.addEventListener("mouseout", (event) => {
  if (!event.relatedTarget) scheduleHeroReset();
});
window.addEventListener("blur", scheduleHeroReset);
deviceStage?.addEventListener("pointermove", tiltDevice);
deviceStage?.addEventListener("pointerleave", () => {
  if (device) device.style.transform = "";
});
window.addEventListener("scroll", updateScroll, { passive: true });
window.addEventListener("resize", () => {
  heroRect = null;
  applyHeroProgress(heroProgress);
}, { passive: true });
updateScroll();
applyHeroProgress(heroProgress);
if (!mobileHero.matches && !reducedMotion.matches) {
  heroIntroFrame = requestAnimationFrame(runHeroIntro);
}

const observer = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add("is-visible");
      observer.unobserve(entry.target);
    }
  });
}, { threshold: .12, rootMargin: "0px 0px -8% 0px" });

document.querySelectorAll(".reveal").forEach((element) => observer.observe(element));

if (hero) {
  const heroVisibilityObserver = new IntersectionObserver(([entry]) => {
    heroVisible = entry.isIntersecting;
    if (!heroVisible) {
      cancelHeroReset();
      heroRect = null;
      hero.classList.remove("is-interacting");
    }
  }, { threshold: 0 });
  heroVisibilityObserver.observe(hero);
}
